/**
 * License Intelligence Header Component
 * Displays page title, compliance score, last sync info, and action buttons
 */

import React from 'react';
import { Button, Icon, Typography } from '@saviynt/design-system';

import { useInvertedLocalize } from '../../../../../shared/hooks';
import ThemeToggle from '../ThemeToggle/ThemeToggle';

import ComplianceScoreChip from './ComplianceScoreChip';
import type { HeaderProps } from './type';

import './Header.css';

/**
 * Header component for License Intelligence page. Shows title, compliance score badge, sync info, and action buttons.
 */
function Header({
  complianceScore,
  lastSyncDate,
  onAddLicense,
}: HeaderProps): React.ReactElement {
  const localize = useInvertedLocalize();

  return (
    <div className='licenseHeader'>
      {/* Top Row: Title and Sync Info */}
      <div className='licenseHeaderTopRow'>
        <Typography kind='h1'>{localize('License Intelligence')}</Typography>
        <div className='licenseHeaderSyncSection'>
          <div className='licenseHeaderSyncText'>
            <Typography kind='body3'>
              {localize('Last data sync:')}
              <span className='licenseHeaderSyncDate'>{lastSyncDate}</span>
            </Typography>
          </div>
          <Button
            className='licenseHeaderInfoBtn'
            type='button'
            aria-label={localize('Data freshness information')}>
            <Icon
              kind='AlertInfoFilled'
              size='small'
              className='licenseHeaderInfoIcon'
            />
          </Button>
          <ThemeToggle />
        </div>
      </div>

      {/* Bottom Row: Compliance Score and Action Buttons */}
      <div className='licenseHeaderBottomRow'>
        <ComplianceScoreChip
          score={complianceScore}
          label={localize('Compliance Score')}
        />
        <div className='licenseHeaderActionsSection'>
          <Button
            className='licenseHeaderAddLicenseBtn'
            onClick={onAddLicense}
            aria-label={localize('Add License')}>
            <div className='licenseHeaderAddLicenseBtnContent'>
              <Icon
                kind='Add'
                size='small'
                className='licenseHeaderAddLicenseIcon'
              />
              <Typography kind='body2-bold'>
                {localize('Add License')}
              </Typography>
            </div>
          </Button>
        </div>
      </div>
    </div>
  );
}

export default Header;
